import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { 
  Search, Filter, Plus, ChevronDown, Phone, Mail, ArrowUpRight,
  Flame, ThermometerSun, Snowflake, Clock, Download, Upload, AlertTriangle, X, UserPlus, CheckCircle, Wind,
  Send, Tag, Users, ListTodo, FileDown, ArrowRightLeft, CheckSquare, Link2,
  Building2, Megaphone
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import Layout from "../components/Layout";
import { userDisplayName } from "../utils/userDisplay";
import DynamicFormRenderer, { useUTMParams } from "../components/DynamicFormRenderer";
import { API, useProject } from "../App";
import { toast } from "sonner";
import { SOURCES, CONFIGURATIONS, BUDGET_PRESETS, LOCALITIES } from "../constants/dropdownOptions";
import SourceBasisToggle from "../components/SourceBasisToggle";
import SavedViewsBar from "../components/SavedViewsBar";
import { DateRangePicker } from "../components/DateRangePicker";
import { appendDateParams } from "../utils/dateFilters";
import { usePermissions } from "../hooks/usePermissions";
import { useUrlFilters } from "../hooks/useUrlFilters";
import { useScrollRestore } from "../hooks/useScrollRestore";

const LeadsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canViewAllLeads } = usePermissions();
  const { selectedProjectId, selectedProject } = useProject();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;
  const [filters, setFilters, resetFilters, activeFilterCount] = useUrlFilters({
    stage: "",
    score: "",
    source: "",
    sub_source: "",
    awareness: "",
    search: "",
    project: "",
    campaign: "",
    assigned_to: "",
    date_from: "",
    date_to: "",
    is_repeat_buyer: false,
    focus: "active_pipeline",
    source_basis: "latest",
    archived: false,
  }, { preserveKeys: ["new"] });
  const focusView = filters.focus;
  const setFocusView = (v) => setFilters({ focus: v });
  const sourceBasis = filters.source_basis;
  const setSourceBasis = (v) => setFilters({ source_basis: v });
  const showArchived = filters.archived;
  const setShowArchived = (v) => setFilters({ archived: v });
  useScrollRestore([leads.length]);
  const [users, setUsers] = useState([]);
  const [userMap, setUserMap] = useState({});
  const [showNewDialog, setShowNewDialog] = useState(searchParams.get('new') === 'true');
  const [subSourceOptions, setSubSourceOptions] = useState([]);

  // Bulk selection state
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [bulkDialog, setBulkDialog] = useState({ type: null, open: false });
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkMsgChannel, setBulkMsgChannel] = useState("whatsapp");
  const [bulkMsgTemplate, setBulkMsgTemplate] = useState("");
  const [bulkStage, setBulkStage] = useState("");
  const [bulkStageReason, setBulkStageReason] = useState("");
  const [bulkAssignTo, setBulkAssignTo] = useState("");
  const [bulkTags, setBulkTags] = useState("");
  const [bulkTagAction, setBulkTagAction] = useState("add");
  const [bulkTaskTitle, setBulkTaskTitle] = useState("");
  const [bulkTaskType, setBulkTaskType] = useState("call");
  const [bulkTaskPriority, setBulkTaskPriority] = useState("medium");
  const [bulkTaskDue, setBulkTaskDue] = useState("");
  const [bulkProject, setBulkProject] = useState("");
  const [bulkScore, setBulkScore] = useState("");
  const [bulkSourceVal, setBulkSourceVal] = useState("");
  const [bulkSourceSubVal, setBulkSourceSubVal] = useState("");
  const [channelTemplates, setChannelTemplates] = useState([]);

  // Reset form when dialog closes
  const handleDialogChange = (open) => {
    setShowNewDialog(open);
    if (!open) {
      // Reset form when closing
      setNewLead({
        name: "", phone: "", email: "", source: "website",
        project_interest: "", budget_min: "", budget_max: "",
        configuration: "", locality_preference: "", notes: "", referrer_id: ""
      });
      setDuplicateWarning(null);
      setShowNewReferrerForm(false);
      setNewReferrer({ type: "", name: "", phone: "", email: "", relationship: "" });
    }
  };
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const fileInputRef = useRef(null);
  const [newLead, setNewLead] = useState({
    name: "",
    phone: "",
    email: "",
    source: "website",
    project_interest: "",
    budget_min: "",
    budget_max: "",
    configuration: "",
    locality_preference: "",
    notes: "",
    referrer_id: ""
  });
  const [projects, setProjects] = useState([]);
  
  // Referrer state
  const [referrers, setReferrers] = useState([]);
  const [referrerSearch, setReferrerSearch] = useState("");
  const [showNewReferrerForm, setShowNewReferrerForm] = useState(false);
  const [newReferrer, setNewReferrer] = useState({
    type: "",
    name: "",
    phone: "",
    email: "",
    relationship: ""
  });
  const [creatingReferrer, setCreatingReferrer] = useState(false);
  
  // UTM params from URL
  const utmParams = useUTMParams();

  // Data Health cross-page filter
  const [dhFilter, setDhFilter] = useState(() => {
    if (searchParams.get("dh_filter") === "true") {
      const ids = sessionStorage.getItem("dh_filter_ids");
      const title = sessionStorage.getItem("dh_filter_title");
      if (ids) {
        return { active: true, title: title || "Data Health Issue", ids: JSON.parse(ids) };
      }
    }
    return { active: false, title: "", ids: [] };
  });

  const clearDhFilter = () => {
    sessionStorage.removeItem("dh_filter_ids");
    sessionStorage.removeItem("dh_filter_title");
    setDhFilter({ active: false, title: "", ids: [] });
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete("dh_filter");
      return params;
    });
  };

  useEffect(() => {
    setPage(1);
  }, [filters]);

  useEffect(() => {
    fetchLeads();
  }, [filters, page, dhFilter.active, selectedProjectId]);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    axios.get(`${API}/governance/users/all`).then(res => {
      const active = (res.data || []).filter(u => u.status === "active");
      setUsers(active);
      const map = {};
      active.forEach(u => { map[u.user_id] = userDisplayName(u); });
      setUserMap(map);
    }).catch(() => {});
  }, []);

  // Fetch sub-sources when source filter changes
  useEffect(() => {
    if (filters.source) {
      axios.get(`${API}/leads/sub-sources/${filters.source}`).then(res => {
        setSubSourceOptions(res.data || []);
      }).catch(() => setSubSourceOptions([]));
    } else {
      setSubSourceOptions([]);
      if (filters.sub_source) setFilters(f => ({ ...f, sub_source: "" }));
    }
  }, [filters.source]);


  const fetchLeads = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.stage) params.append('stage', filters.stage);
      if (filters.score) params.append('score', filters.score);
      if (filters.source) params.append('source', filters.source);
      if (filters.source) params.append('source_basis', sourceBasis);
      if (filters.sub_source) params.append('sub_source', filters.sub_source);
      if (filters.awareness) params.append('awareness', filters.awareness);
      if (filters.search) params.append('search', filters.search);
      if (filters.project) params.append('project', filters.project);
      else if (selectedProjectId !== "all" && selectedProject) params.append('project', selectedProject.name);
      if (filters.campaign) params.append('campaign', filters.campaign);
      if (filters.assigned_to) params.append('assigned_to', filters.assigned_to);
      if (filters.is_repeat_buyer) params.append('is_repeat_buyer', 'true');
      if (focusView && focusView !== "all" && !showArchived) params.append('focus', focusView);
      if (showArchived) params.append('archived', 'true');
      // Data health filter: pass lead_ids
      if (dhFilter.active && dhFilter.ids.length > 0) {
        params.append('lead_ids', dhFilter.ids.join(','));
      }
      appendDateParams(params, filters.date_from, filters.date_to);
      params.append('skip', String((page - 1) * PAGE_SIZE));
      params.append('limit', String(PAGE_SIZE));
      
      const response = await axios.get(`${API}/leads?${params.toString()}`);
      setLeads(response.data.leads);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Fetch leads error:", error);
      toast.error("Failed to fetch leads");
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error("Fetch projects error:", error);
    }
  };

  // Fetch referrers when source is referral or channel_partner
  const fetchReferrers = async () => {
    try {
      const response = await axios.get(`${API}/referrals/referrers`);
      setReferrers(response.data);
    } catch (error) {
      console.error("Fetch referrers error:", error);
    }
  };

  // Load referrers when source changes to referral/channel_partner
  useEffect(() => {
    if (newLead.source === "referral" || newLead.source === "channel_partner") {
      fetchReferrers();
    } else {
      setReferrers([]);
      setNewLead(prev => ({ ...prev, referrer_id: "" }));
    }
    setShowNewReferrerForm(false);
  }, [newLead.source]);

  // Create new referrer inline
  const handleCreateReferrer = async () => {
    if (!newReferrer.name || !newReferrer.phone) {
      toast.error("Please enter referrer name and phone");
      return;
    }
    
    // For referral source, type must be selected
    if (newLead.source === "referral" && !newReferrer.type) {
      toast.error("Please select referrer type");
      return;
    }
    
    setCreatingReferrer(true);
    try {
      const referrerType = newLead.source === "channel_partner" ? "channel_partner" : newReferrer.type;
      
      const payload = {
        type: referrerType,
        name: newReferrer.name,
        phone: newReferrer.phone,
        email: newReferrer.email || null,
        other_details: (referrerType === "other" && newReferrer.relationship) 
          ? { relationship: newReferrer.relationship } 
          : null
      };
      const res = await axios.post(`${API}/referrals/referrers`, payload);
      toast.success(newLead.source === "channel_partner" ? "Channel Partner added" : "Referrer added");
      setNewLead(prev => ({ ...prev, referrer_id: res.data.referrer_id }));
      setShowNewReferrerForm(false);
      setNewReferrer({ type: "", name: "", phone: "", email: "", relationship: "" });
      fetchReferrers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create referrer");
    }
    setCreatingReferrer(false);
  };

  // Check for duplicate when phone or email changes
  const checkDuplicate = async (phone, email) => {
    if (!phone && !email) {
      setDuplicateWarning(null);
      return;
    }
    
    setCheckingDuplicate(true);
    try {
      const response = await axios.post(`${API}/leads/check-duplicate`, { phone, email });
      if (response.data.is_duplicate) {
        setDuplicateWarning(response.data);
      } else {
        setDuplicateWarning(null);
      }
    } catch (error) {
      console.error("Duplicate check error:", error);
    } finally {
      setCheckingDuplicate(false);
    }
  };

  // Debounce duplicate check
  useEffect(() => {
    const timer = setTimeout(() => {
      if (newLead.phone || newLead.email) {
        checkDuplicate(newLead.phone, newLead.email);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [newLead.phone, newLead.email]);

  const handleCreateLead = async () => {
    if (!newLead.name || !newLead.phone) {
      toast.error("Name and phone are required");
      return;
    }
    
    // Validate referrer for referral/channel_partner sources
    if ((newLead.source === "referral" || newLead.source === "channel_partner") && !newLead.referrer_id) {
      toast.error("Please select or add a referrer");
      return;
    }

    try {
      const leadData = {
        ...newLead,
        budget_min: newLead.budget_min ? parseFloat(newLead.budget_min) : null,
        budget_max: newLead.budget_max ? parseFloat(newLead.budget_max) : null
      };
      
      const response = await axios.post(`${API}/leads`, leadData);
      
      // If lead has a referrer, create the referral link
      if (newLead.referrer_id && response.data.lead_id) {
        try {
          await axios.post(`${API}/referrals`, {
            referrer_id: newLead.referrer_id,
            lead_id: response.data.lead_id
          });
        } catch (refErr) {
          console.error("Failed to link referral:", refErr);
          // Don't block lead creation if referral linking fails
        }
      }
      
      // Handle different duplicate actions from backend
      if (response.data.duplicate_action === "reinquiry") {
        toast.success(response.data.message || "Re-inquiry recorded on existing lead", { duration: 5000 });
      } else if (response.data.duplicate_action === "linked") {
        toast.success(response.data.message || `Linked lead created (parent: ${response.data.parent_lead_name})`, { duration: 5000 });
      } else {
        toast.success("Lead created successfully");
      }
      
      setShowNewDialog(false);
      setDuplicateWarning(null);
      setShowNewReferrerForm(false);
      setNewLead({
        name: "", phone: "", email: "", source: "website",
        project_interest: "", budget_min: "", budget_max: "",
        configuration: "", locality_preference: "", notes: "", referrer_id: ""
      });
      fetchLeads();
      navigate(`/leads/${response.data.lead_id}`);
    } catch (error) {
      console.error("Create lead error:", error);
      toast.error("Failed to create lead");
    }
  };

  const handleImportCSV = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImportLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/leads/import/csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        toast.success(`Imported ${response.data.created} new, ${response.data.linked || 0} linked. ${response.data.duplicates} duplicates skipped.`);
        if (response.data.total_errors > 0) {
          toast.warning(`${response.data.total_errors} rows had errors`);
        }
        fetchLeads();
        setShowImportDialog(false);
      }
    } catch (error) {
      console.error("Import error:", error);
      toast.error("Failed to import leads");
    } finally {
      setImportLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/leads/import/template`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'lead_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  const formatINR = (amount) => {
    if (!amount) return "N/A";
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)} L`;
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const getScoreBadge = (score) => {
    const styles = {
      hot: "bg-red-100 text-red-700",
      warm: "bg-amber-100 text-amber-700",
      nurture: "bg-blue-100 text-blue-700",
      cold: "bg-sky-100 text-sky-600",
      dormant: "bg-slate-100 text-slate-500",
      converted: "bg-green-100 text-green-700",
      new: "bg-emerald-100 text-emerald-700"
    };
    return styles[score] || styles.new;
  };

  const getScoreIcon = (score) => {
    const icons = {
      hot: <Flame className="w-3 h-3" />,
      warm: <ThermometerSun className="w-3 h-3" />,
      nurture: <Snowflake className="w-3 h-3" />,
      cold: <Wind className="w-3 h-3" />,
      dormant: <Clock className="w-3 h-3" />,
      converted: <CheckCircle className="w-3 h-3" />
    };
    return icons[score] || null;
  };

  const getStageBadge = (stage) => {
    const styles = {
      new: "bg-emerald-50 text-emerald-700",
      contacted: "bg-blue-50 text-blue-700",
      qualified: "bg-indigo-50 text-indigo-700",
      site_visit_scheduled: "bg-orange-50 text-orange-700",
      site_visit: "bg-purple-50 text-purple-700",
      revisit: "bg-fuchsia-50 text-fuchsia-700",
      negotiation_scheduled: "bg-cyan-50 text-cyan-700",
      negotiation: "bg-amber-50 text-amber-700",
      booked: "bg-green-50 text-green-700",
      re_engaged: "bg-rose-50 text-rose-700",
      lost: "bg-red-50 text-red-700"
    };
    return styles[stage] || styles.new;
  };

  const getAwarenessBadge = (awareness) => {
    const styles = {
      "Completely Unaware": "bg-slate-100 text-slate-600 border border-slate-200",
      "Problem Aware": "bg-amber-50 text-amber-700 border border-amber-200",
      "Solution Aware": "bg-blue-50 text-blue-700 border border-blue-200",
      "Product Aware": "bg-purple-50 text-purple-700 border border-purple-200",
      "Most Aware": "bg-green-50 text-green-700 border border-green-200"
    };
    return styles[awareness] || styles["Completely Unaware"];
  };

  const getAwarenessIcon = (awareness) => {
    // Simple circle indicators for awareness stages (1-5 dots)
    const levels = {
      "Completely Unaware": "○",
      "Problem Aware": "◐",
      "Solution Aware": "◑",
      "Product Aware": "◕",
      "Most Aware": "●"
    };
    return <span className="text-[10px]">{levels[awareness] || "○"}</span>;
  };


  const toggleSelectLead = (leadId) => {
    setSelectedLeads(prev => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId);
      else next.add(leadId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedLeads.size === leads.length) {
      setSelectedLeads(new Set());
    } else {
      setSelectedLeads(new Set(leads.map(l => l.lead_id)));
    }
  };

  const openBulkDialog = async (type) => {
    setBulkDialog({ type, open: true });
    // Reset per-type inputs so a previous selection doesn't leak across actions
    if (type === "project") setBulkProject("");
    if (type === "score") setBulkScore("");
    if (type === "source") { setBulkSourceVal(""); setBulkSourceSubVal(""); }
    if (type === "message") {
      setBulkMsgChannel("whatsapp");
      setBulkMsgTemplate("");
      try {
        const res = await axios.get(`${API}/message-templates/approved?channel=whatsapp`);
        setChannelTemplates(res.data);
      } catch { setChannelTemplates([]); }
    }
  };

  const fetchTemplatesForChannel = async (ch) => {
    setBulkMsgChannel(ch);
    setBulkMsgTemplate("");
    try {
      const res = await axios.get(`${API}/message-templates/approved?channel=${ch}`);
      setChannelTemplates(res.data);
    } catch { setChannelTemplates([]); }
  };

  const handleBulkAction = async () => {
    const ids = Array.from(selectedLeads);
    if (ids.length === 0) return;
    setBulkLoading(true);
    try {
      let res;
      switch (bulkDialog.type) {
        case "message":
          if (!bulkMsgTemplate) { toast.error("Select a template"); setBulkLoading(false); return; }
          res = await axios.post(`${API}/leads/bulk/message`, { lead_ids: ids, template_id: bulkMsgTemplate, channel: bulkMsgChannel });
          toast.success(`${res.data.sent} messages queued${res.data.skipped > 0 ? `, ${res.data.skipped} skipped` : ""}`);
          break;
        case "stage":
          if (!bulkStage) { toast.error("Select a stage"); setBulkLoading(false); return; }
          res = await axios.post(`${API}/leads/bulk/stage`, { lead_ids: ids, stage: bulkStage, reason: bulkStageReason || undefined });
          toast.success(`${res.data.updated} leads moved to ${bulkStage.replace(/_/g, " ")}`);
          break;
        case "assign":
          if (!bulkAssignTo) { toast.error("Select a user"); setBulkLoading(false); return; }
          res = await axios.post(`${API}/leads/bulk/assign`, { lead_ids: ids, assigned_to: bulkAssignTo });
          toast.success(`${res.data.updated} leads reassigned to ${res.data.assigned_to_name}`);
          break;
        case "tag":
          if (!bulkTags.trim()) { toast.error("Enter tags"); setBulkLoading(false); return; }
          res = await axios.post(`${API}/leads/bulk/tag`, { lead_ids: ids, tags: bulkTags.split(",").map(t => t.trim()).filter(Boolean), action: bulkTagAction });
          toast.success(`Tags ${bulkTagAction === "add" ? "added to" : "removed from"} ${res.data.updated} leads`);
          break;
        case "task":
          if (!bulkTaskTitle.trim()) { toast.error("Enter task title"); setBulkLoading(false); return; }
          res = await axios.post(`${API}/leads/bulk/task`, { lead_ids: ids, title: bulkTaskTitle, task_type: bulkTaskType, priority: bulkTaskPriority, due_date: bulkTaskDue || undefined });
          toast.success(`Task created for ${res.data.created} leads`);
          break;
        case "project":
          if (!bulkProject) { toast.error("Select a project"); setBulkLoading(false); return; }
          res = await axios.post(`${API}/leads/bulk/project`, { lead_ids: ids, project_interest: bulkProject });
          toast.success(`${res.data.updated} leads set to project: ${res.data.project}`);
          break;
        case "score":
          if (!bulkScore) { toast.error("Select a score"); setBulkLoading(false); return; }
          res = await axios.post(`${API}/leads/bulk/score`, { lead_ids: ids, score: bulkScore });
          toast.success(`${res.data.updated} leads marked as ${res.data.score}`);
          break;
        case "source":
          if (!bulkSourceVal) { toast.error("Select a source"); setBulkLoading(false); return; }
          res = await axios.post(`${API}/leads/bulk/source`, {
            lead_ids: ids,
            source: bulkSourceVal,
            sub_source: bulkSourceSubVal || undefined,
          });
          toast.success(`${res.data.updated} leads tagged with source ${res.data.source}`);
          break;
        case "export":
          const expRes = await axios.post(`${API}/leads/bulk/export`, { lead_ids: ids }, { responseType: "blob" });
          const url = window.URL.createObjectURL(new Blob([expRes.data]));
          const a = document.createElement("a"); a.href = url; a.download = `leads_export_${new Date().toISOString().slice(0,10)}.csv`; a.click();
          toast.success(`Exported ${ids.length} leads`);
          break;
        default: break;
      }
      setBulkDialog({ type: null, open: false });
      setSelectedLeads(new Set());
      fetchLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk action failed");
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 space-y-6" data-testid="leads-page">
        {/* Data Health Filter Banner */}
        {dhFilter.active && (
          <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3" data-testid="dh-filter-banner">
            <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-amber-800">
                Filtered by Data Health: <span className="font-bold">{dhFilter.title}</span>
              </p>
              <p className="text-xs text-amber-600">{dhFilter.ids.length} affected leads shown</p>
            </div>
            <Button variant="outline" size="sm" onClick={clearDhFilter} className="shrink-0 border-amber-300 text-amber-700 hover:bg-amber-100" data-testid="clear-dh-filter">
              <X className="w-3.5 h-3.5 mr-1" /> Clear Filter
            </Button>
          </div>
        )}
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Manrope']">Leads</h1>
            <p className="text-slate-500 mt-1">{total} {showArchived ? "archived" : (focusView === "all" ? "total" : focusView === "hot" ? "hot & warm" : focusView === "follow_up" ? "needs follow-up" : focusView === "new_today" ? "new today" : "active")} leads{total > PAGE_SIZE ? ` · Page ${page} of ${Math.ceil(total / PAGE_SIZE)}` : ""}</p>
          </div>
          <div className="flex gap-2">
            <Dialog open={showNewDialog} onOpenChange={handleDialogChange}>
              <DialogTrigger asChild>
                <Button 
                  data-testid="create-lead-btn"
                  className="bg-slate-900 hover:bg-slate-800 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Lead
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-['Manrope']">Create New Lead</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  {/* Source Selection */}
                  <div>
                    <Label>Lead Source *</Label>
                    <Select value={newLead.source} onValueChange={(v) => setNewLead({...newLead, source: v, referrer_id: ""})}>
                      <SelectTrigger data-testid="lead-source-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {SOURCES.map(s => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Referrer Selection */}
                  {newLead.source === "referral" && (
                    <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
                      <div className="flex items-center justify-between mb-3">
                        <Label className="text-sm font-medium text-blue-800">Referred By *</Label>
                        <Button type="button" variant="ghost" size="sm" onClick={() => setShowNewReferrerForm(!showNewReferrerForm)} className="text-xs text-blue-700">
                          <UserPlus className="w-3 h-3 mr-1" />{showNewReferrerForm ? "Select Existing" : "Add New"}
                        </Button>
                      </div>
                      {!showNewReferrerForm ? (
                        <Select value={newLead.referrer_id} onValueChange={(v) => setNewLead({...newLead, referrer_id: v})}>
                          <SelectTrigger className="bg-white"><SelectValue placeholder="Select referrer" /></SelectTrigger>
                          <SelectContent>{referrers.filter(r => r.type !== "channel_partner").map(r => (<SelectItem key={r.referrer_id} value={r.referrer_id}>{r.name}</SelectItem>))}</SelectContent>
                        </Select>
                      ) : (
                        <div className="space-y-3 bg-white p-3 rounded-lg">
                          <Select value={newReferrer.type} onValueChange={(v) => setNewReferrer({...newReferrer, type: v})}>
                            <SelectTrigger><SelectValue placeholder="Type *" /></SelectTrigger>
                            <SelectContent><SelectItem value="existing_customer">Customer</SelectItem><SelectItem value="employee">Employee</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
                          </Select>
                          <div className="grid grid-cols-2 gap-2">
                            <Input placeholder="Name *" value={newReferrer.name} onChange={(e) => setNewReferrer({...newReferrer, name: e.target.value})} />
                            <Input placeholder="Phone *" value={newReferrer.phone} onChange={(e) => setNewReferrer({...newReferrer, phone: e.target.value})} />
                          </div>
                          <Button type="button" size="sm" onClick={handleCreateReferrer} disabled={creatingReferrer} className="w-full">{creatingReferrer ? "Adding..." : "Add Referrer"}</Button>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Channel Partner Selection */}
                  {newLead.source === "channel_partner" && (
                    <div className="border rounded-lg p-4 bg-purple-50 border-purple-200">
                      <div className="flex items-center justify-between mb-3">
                        <Label className="text-sm font-medium text-purple-800">Channel Partner *</Label>
                        <Button type="button" variant="ghost" size="sm" onClick={() => setShowNewReferrerForm(!showNewReferrerForm)} className="text-xs text-purple-700">
                          <UserPlus className="w-3 h-3 mr-1" />{showNewReferrerForm ? "Select Existing" : "Add New"}
                        </Button>
                      </div>
                      {!showNewReferrerForm ? (
                        <Select value={newLead.referrer_id} onValueChange={(v) => setNewLead({...newLead, referrer_id: v})}>
                          <SelectTrigger className="bg-white"><SelectValue placeholder="Select partner" /></SelectTrigger>
                          <SelectContent>{referrers.filter(r => r.type === "channel_partner").map(r => (<SelectItem key={r.referrer_id} value={r.referrer_id}>{r.name}</SelectItem>))}</SelectContent>
                        </Select>
                      ) : (
                        <div className="space-y-3 bg-white p-3 rounded-lg">
                          <div className="grid grid-cols-2 gap-2">
                            <Input placeholder="Name *" value={newReferrer.name} onChange={(e) => setNewReferrer({...newReferrer, name: e.target.value, type: "channel_partner"})} />
                            <Input placeholder="Phone *" value={newReferrer.phone} onChange={(e) => setNewReferrer({...newReferrer, phone: e.target.value, type: "channel_partner"})} />
                          </div>
                          <Button type="button" size="sm" onClick={() => { setNewReferrer(prev => ({...prev, type: "channel_partner"})); handleCreateReferrer(); }} disabled={creatingReferrer} className="w-full bg-purple-600 hover:bg-purple-700">{creatingReferrer ? "Adding..." : "Add Partner"}</Button>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Dynamic Form Fields from Form Builder */}
                  <DynamicFormRenderer
                    formType="lead_internal"
                    values={{...newLead, ...utmParams}}
                    onChange={(fieldName, value) => setNewLead(prev => ({...prev, [fieldName]: value}))}
                    projects={projects}
                    excludeFields={["source"]}
                    showGroups={true}
                    gridCols={2}
                  />
                  
                  {/* Duplicate Warning - Informational */}
                  {duplicateWarning && (
                    <Alert className="border-blue-200 bg-blue-50">
                      <AlertTriangle className="h-4 w-4 text-blue-600" />
                      <AlertDescription className="text-blue-800">
                        <p className="font-medium mb-2">Existing lead(s) found with same {duplicateWarning.match_type}:</p>
                        <ul className="text-sm space-y-1.5">
                          {duplicateWarning.existing_leads.map(lead => (
                            <li key={lead.lead_id} className="flex items-center gap-2 flex-wrap bg-white/60 px-2 py-1.5 rounded">
                              <span className="font-medium">{lead.name}</span>
                              <span className="text-blue-600">{lead.phone}</span>
                              <span className="text-xs bg-blue-200 px-1.5 py-0.5 rounded capitalize">{lead.stage?.replace(/_/g, ' ')}</span>
                              {lead.project_interest && <span className="text-xs bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded">{lead.project_interest}</span>}
                              <button 
                                className="text-blue-700 underline text-xs ml-auto"
                                onClick={(e) => { e.stopPropagation(); navigate(`/leads/${lead.lead_id}`); }}
                              >
                                View Lead
                              </button>
                            </li>
                          ))}
                        </ul>
                        <p className="text-xs mt-2 text-blue-700 bg-blue-100 px-2 py-1.5 rounded">
                          <strong>What happens on create:</strong> 
                          {newLead.project_interest && duplicateWarning.existing_leads.some(l => l.project_interest?.toLowerCase() === newLead.project_interest?.toLowerCase())
                            ? " Same project → Re-inquiry will be logged on existing lead"
                            : " Different project → New linked lead will be created"}
                        </p>
                      </AlertDescription>
                    </Alert>
                  )}
                  
                  <div className="flex gap-2">
                    <Button 
                      variant="outline"
                      className="flex-1"
                      onClick={() => { setShowNewDialog(false); setDuplicateWarning(null); }}
                    >
                      Cancel
                    </Button>
                    <Button 
                      data-testid="submit-lead-btn"
                      className="flex-1 bg-slate-900 hover:bg-slate-800"
                      onClick={handleCreateLead}
                      disabled={checkingDuplicate}
                    >
                      {checkingDuplicate ? "Checking..." : duplicateWarning ? "Create Lead Anyway" : "Create Lead"}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            
            {/* Import Dialog */}
            <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="import-leads-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  Import
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-['Manrope']">Import Leads from CSV</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <p className="text-sm text-slate-600">
                    Upload a CSV file with your leads. Download the template below to see the required format.
                  </p>
                  
                  <Button variant="outline" onClick={downloadTemplate} className="w-full" data-testid="download-csv-template-btn">
                    <Download className="w-4 h-4 mr-2" />
                    Download CSV Template
                  </Button>
                  
                  <div className="relative border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                    <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                    <p className="text-sm text-slate-600 mb-2">Click to upload or drag and drop</p>
                    <p className="text-xs text-slate-400">CSV files only</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      onChange={handleImportCSV}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      disabled={importLoading}
                    />
                  </div>
                  
                  {importLoading && (
                    <div className="flex items-center justify-center gap-2 text-slate-600">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-600"></div>
                      Importing leads...
                    </div>
                  )}
                  
                  <div className="text-xs text-slate-500">
                    <p className="font-medium mb-1">CSV columns:</p>
                    <p>name, phone, email, source, project_interest, budget_min, budget_max, configuration, locality, notes</p>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Smart Focus View */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="focus-view-bar">
          {[
            { key: "active_pipeline", label: "Active Pipeline" },
            { key: "hot", label: "Hot & Warm" },
            { key: "follow_up", label: "Needs Follow-up" },
            { key: "new_today", label: "New Today" },
            { key: "all", label: "All Leads" },
          ].map((v) => (
            <button key={v.key} onClick={() => setFilters({ focus: v.key, archived: false })}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${focusView === v.key && !showArchived ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
              data-testid={`focus-${v.key}`}>
              {v.label}
            </button>
          ))}
          <div className="w-px h-5 bg-slate-200 mx-1" />
          <button onClick={() => setFilters({ archived: !showArchived, focus: !showArchived ? "all" : "active_pipeline" })}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${showArchived ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
            data-testid="toggle-archived">
            Archived
          </button>
        </div>

        {/* Filters */}
        <Card className="bg-white border-slate-200">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row md:flex-wrap gap-3 items-stretch md:items-center">
              <div className="flex-1 min-w-[200px] relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  data-testid="lead-search-input"
                  placeholder="Search by name, phone, or email..."
                  className="pl-10"
                  value={filters.search}
                  onChange={(e) => setFilters({...filters, search: e.target.value})}
                />
              </div>
              <Select value={filters.project || "all"} onValueChange={(v) => setFilters({...filters, project: v === "all" ? "" : v})}>
                <SelectTrigger className="w-full md:w-44" data-testid="filter-project-select">
                  <SelectValue placeholder="All Projects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  <SelectItem value="__unassigned__" data-testid="filter-project-unassigned">— No Project Set</SelectItem>
                  {projects.map(p => (
                    <SelectItem key={p.project_id} value={p.name}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filters.stage || "all"} onValueChange={(v) => setFilters({...filters, stage: v === "all" ? "" : v})}>
                <SelectTrigger className="w-full md:w-40" data-testid="filter-stage-select">
                  <SelectValue placeholder="All Stages" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stages</SelectItem>
                  <SelectItem value="__unassigned__" data-testid="filter-stage-unassigned">— No Stage Set</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="qualified">Qualified</SelectItem>
                  <SelectItem value="site_visit_scheduled">SV Scheduled</SelectItem>
                  <SelectItem value="site_visit">Site Visit</SelectItem>
                  <SelectItem value="revisit">Revisit</SelectItem>
                  <SelectItem value="negotiation_scheduled">Nego Scheduled</SelectItem>
                  <SelectItem value="negotiation">Negotiation</SelectItem>
                  <SelectItem value="booked">Booked</SelectItem>
                  <SelectItem value="re_engaged">Re-Engaged</SelectItem>
                  <SelectItem value="lost">Lost</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filters.score || "all"} onValueChange={(v) => setFilters({...filters, score: v === "all" ? "" : v})}>
                <SelectTrigger className="w-full md:w-40" data-testid="filter-score-select">
                  <SelectValue placeholder="All Scores" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Scores</SelectItem>
                  <SelectItem value="__unassigned__" data-testid="filter-score-unassigned">— No Score Set</SelectItem>
                  <SelectItem value="hot">Hot</SelectItem>
                  <SelectItem value="warm">Warm</SelectItem>
                  <SelectItem value="nurture">Nurture</SelectItem>
                  <SelectItem value="cold">Cold</SelectItem>
                  <SelectItem value="dormant">Dormant</SelectItem>
                  <SelectItem value="converted">Converted</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filters.awareness || "all"} onValueChange={(v) => setFilters({...filters, awareness: v === "all" ? "" : v})}>
                <SelectTrigger className="w-full md:w-44" data-testid="filter-awareness-select">
                  <SelectValue placeholder="All Awareness" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Awareness</SelectItem>
                  <SelectItem value="__unassigned__" data-testid="filter-awareness-unassigned">— Not Tagged</SelectItem>
                  <SelectItem value="Completely Unaware">Unaware</SelectItem>
                  <SelectItem value="Problem Aware">Problem Aware</SelectItem>
                  <SelectItem value="Solution Aware">Solution Aware</SelectItem>
                  <SelectItem value="Product Aware">Product Aware</SelectItem>
                  <SelectItem value="Most Aware">Most Aware</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filters.source || "all"} onValueChange={(v) => setFilters({...filters, source: v === "all" ? "" : v, sub_source: ""})}>
                <SelectTrigger className="w-full md:w-40" data-testid="filter-source-select">
                  <SelectValue placeholder="All Sources">
                    {filters.source === "__unassigned__"
                      ? "— No Source Set"
                      : filters.source
                        ? (SOURCES.find(s => s.value === filters.source)?.label
                            || `${filters.source} (legacy)`)
                        : "All Sources"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  <SelectItem value="__unassigned__" data-testid="filter-source-unassigned">— No Source Set</SelectItem>
                  {filters.source && !SOURCES.some(s => s.value === filters.source) && filters.source !== "__unassigned__" && (
                    <SelectItem value={filters.source}>{filters.source} (legacy)</SelectItem>
                  )}
                  {SOURCES.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {filters.source && (
                <SourceBasisToggle value={sourceBasis} onChange={setSourceBasis} />
              )}
              {subSourceOptions.length > 0 && (
                <Select value={filters.sub_source || "all"} onValueChange={(v) => setFilters({...filters, sub_source: v === "all" ? "" : v})}>
                  <SelectTrigger className="w-full md:w-44" data-testid="filter-sub-source-select">
                    <SelectValue placeholder="All Sub-sources" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sub-sources</SelectItem>
                    <SelectItem value="__unassigned__" data-testid="filter-sub-source-unassigned">— No Sub-source</SelectItem>
                    {subSourceOptions.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label} ({s.count})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {canViewAllLeads && (
              <Select value={filters.assigned_to || "all"} onValueChange={(v) => setFilters({...filters, assigned_to: v === "all" ? "" : v})}>
                <SelectTrigger className="w-full md:w-44" data-testid="filter-assigned-to-select">
                  <SelectValue placeholder="All Assignees" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Assignees</SelectItem>
                  <SelectItem value="__unassigned__" data-testid="filter-assigned-to-unassigned">— Unassigned</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              )}
              <DateRangePicker
                dateFrom={filters.date_from}
                dateTo={filters.date_to}
                onChange={(from, to) => setFilters({...filters, date_from: from, date_to: to})}
              />
              {/* Repeat Buyers Toggle */}
              <Button
                variant={filters.is_repeat_buyer ? "default" : "outline"}
                size="sm"
                onClick={() => setFilters({...filters, is_repeat_buyer: !filters.is_repeat_buyer})}
                className={`gap-1.5 ${filters.is_repeat_buyer ? "bg-amber-500 hover:bg-amber-600 text-white" : ""}`}
                data-testid="filter-repeat-buyer-btn"
              >
                <span className="text-base">🏠</span>
                Repeat Buyers
              </Button>
            </div>
            {/* Saved Views bar (always visible) */}
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100 items-center">
              <SavedViewsBar
                path="/leads"
                currentQuery={searchParams.toString()}
                activeFilterCount={activeFilterCount}
              />
            </div>
            {/* Active URL-based filters */}
            {(filters.project || filters.campaign || filters.assigned_to || filters.is_repeat_buyer) && (
              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100">
                <span className="text-xs text-slate-500 leading-6">Active filters:</span>
                {filters.is_repeat_buyer && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-50 text-amber-700 border border-amber-200">
                    🏠 Repeat Buyers
                    <X className="w-3 h-3 cursor-pointer" onClick={() => setFilters({...filters, is_repeat_buyer: false})} />
                  </span>
                )}
                {filters.project && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-violet-50 text-violet-700 border border-violet-200">
                    Project: {filters.project === "__unassigned__" ? "No Project Set" : filters.project}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => setFilters({...filters, project: ""})} />
                  </span>
                )}
                {filters.campaign && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-50 text-amber-700 border border-amber-200">
                    Campaign: {filters.campaign}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => setFilters({...filters, campaign: ""})} />
                  </span>
                )}
                {filters.assigned_to && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-emerald-50 text-emerald-700 border border-emerald-200">
                    Assigned: {filters.assigned_to === "__unassigned__" ? "Unassigned" : (userMap[filters.assigned_to] || filters.assigned_to)}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => setFilters({...filters, assigned_to: ""})} />
                  </span>
                )}
                <div className="flex-1" />
                {activeFilterCount > 0 && (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(window.location.href);
                        toast.success("Filtered URL copied — share with your team");
                      }}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100"
                      data-testid="share-filtered-view-btn"
                      title="Copy this filtered view's URL"
                    >
                      <Link2 className="w-3 h-3" /> Share view
                    </button>
                    <button
                      type="button"
                      onClick={resetFilters}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200"
                      data-testid="reset-filters-btn"
                    >
                      <X className="w-3 h-3" /> Clear all ({activeFilterCount})
                    </button>
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bulk Actions Bar */}
        {selectedLeads.size > 0 && (
          <div className="sticky top-0 z-20 bg-slate-900 text-white rounded-xl px-4 py-3 flex items-center justify-between shadow-lg" data-testid="bulk-actions-bar">
            <div className="flex items-center gap-3">
              <CheckSquare className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-medium">{selectedLeads.size} lead{selectedLeads.size > 1 ? "s" : ""} selected</span>
              <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-300 hover:text-white hover:bg-slate-800" onClick={() => setSelectedLeads(new Set())}>
                Clear
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" className="h-8 text-xs bg-green-600 hover:bg-green-700" onClick={() => openBulkDialog("message")} data-testid="bulk-message-btn">
                <Send className="w-3.5 h-3.5 mr-1" /> Message
              </Button>
              <Button size="sm" variant="secondary" className="h-8 text-xs" onClick={() => openBulkDialog("stage")} data-testid="bulk-stage-btn">
                <ArrowRightLeft className="w-3.5 h-3.5 mr-1" /> Stage
              </Button>
              <Button size="sm" variant="secondary" className="h-8 text-xs" onClick={() => openBulkDialog("assign")} data-testid="bulk-assign-btn">
                <Users className="w-3.5 h-3.5 mr-1" /> Assign
              </Button>
              <Button size="sm" variant="secondary" className="h-8 text-xs" onClick={() => openBulkDialog("project")} data-testid="bulk-project-btn">
                <Building2 className="w-3.5 h-3.5 mr-1" /> Project
              </Button>
              <Button size="sm" variant="secondary" className="h-8 text-xs" onClick={() => openBulkDialog("score")} data-testid="bulk-score-btn">
                <Flame className="w-3.5 h-3.5 mr-1" /> Score
              </Button>
              <Button size="sm" variant="secondary" className="h-8 text-xs" onClick={() => openBulkDialog("source")} data-testid="bulk-source-btn">
                <Megaphone className="w-3.5 h-3.5 mr-1" /> Source
              </Button>
              <Button size="sm" variant="secondary" className="h-8 text-xs" onClick={() => openBulkDialog("tag")} data-testid="bulk-tag-btn">
                <Tag className="w-3.5 h-3.5 mr-1" /> Tag
              </Button>
              <Button size="sm" variant="secondary" className="h-8 text-xs" onClick={() => openBulkDialog("task")} data-testid="bulk-task-btn">
                <ListTodo className="w-3.5 h-3.5 mr-1" /> Task
              </Button>
              <Button size="sm" variant="secondary" className="h-8 text-xs" onClick={() => openBulkDialog("export")} data-testid="bulk-export-btn">
                <FileDown className="w-3.5 h-3.5 mr-1" /> Export
              </Button>
            </div>
          </div>
        )}

        {/* Leads Table */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center gap-4 animate-pulse">
                    <div className="w-10 h-10 bg-slate-200 rounded-full"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-slate-200 rounded w-1/4"></div>
                      <div className="h-3 bg-slate-200 rounded w-1/3"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50">
                      <th className="py-3 px-3 w-10">
                        <Checkbox
                          checked={leads.length > 0 && selectedLeads.size === leads.length}
                          onCheckedChange={toggleSelectAll}
                          data-testid="select-all-checkbox"
                        />
                      </th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4">Lead</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4">Source</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4">Score</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4">Awareness</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4">Stage</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4">Budget</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4">Config</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4">Assigned To</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4">Next Action</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4">Created</th>
                      <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead) => (
                      <tr 
                        key={lead.lead_id} 
                        className={`border-b border-slate-50 hover:bg-slate-50 cursor-pointer transition-colors ${selectedLeads.has(lead.lead_id) ? "bg-blue-50/50" : ""}`}
                        onClick={() => navigate(`/leads/${lead.lead_id}`)}
                        data-testid={`lead-row-${lead.lead_id}`}
                      >
                        <td className="py-3 px-3" onClick={e => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedLeads.has(lead.lead_id)}
                            onCheckedChange={() => toggleSelectLead(lead.lead_id)}
                            data-testid={`select-lead-${lead.lead_id}`}
                          />
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center text-slate-600 font-medium">
                              {lead.name?.charAt(0)?.toUpperCase()}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-slate-900">{lead.name}</p>
                                {lead.is_repeat_buyer && (
                                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-100 text-amber-700 border border-amber-200" title="Repeat Buyer">
                                    🏠 Repeat
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 text-sm text-slate-500">
                                <Phone className="w-3 h-3" />
                                {lead.phone}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm text-slate-600" title={lead.source || ""}>
                            {SOURCES.find(s => s.value === lead.source)?.label
                              || (lead.source ? lead.source.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : "—")}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full capitalize ${getScoreBadge(lead.score)}`}>
                            {getScoreIcon(lead.score)}
                            {lead.score}
                          </span>
                        </td>
                        <td className="py-3 px-4" data-testid={`lead-awareness-${lead.lead_id}`}>
                          {lead.awareness_stage ? (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${getAwarenessBadge(lead.awareness_stage)}`} title={lead.awareness_summary || ""}>
                              {getAwarenessIcon(lead.awareness_stage)}
                              {lead.awareness_stage.replace("Completely ", "").split(" ")[0]}
                            </span>
                          ) : (
                            <span className="text-slate-400 text-xs">-</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full capitalize ${getStageBadge(lead.stage)}`}>
                            {lead.stage?.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">
                          {formatINR(lead.budget_min)} - {formatINR(lead.budget_max)}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">
                          {lead.configuration || '-'}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600" data-testid={`lead-assigned-${lead.lead_id}`}>
                          {userMap[lead.assigned_to] || lead.assigned_to || '-'}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">
                          {lead.next_action?.replace(/_/g, ' ') || '-'}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600 whitespace-nowrap" data-testid={`lead-created-${lead.lead_id}`} title={lead.created_at ? new Date(lead.created_at).toLocaleString() : ""}>
                          {lead.created_at ? new Date(lead.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <ArrowUpRight className="w-4 h-4 text-slate-400" />
                        </td>
                      </tr>
                    ))}
                    {leads.length === 0 && (
                      <tr>
                        <td colSpan="11" className="py-12 text-center text-slate-500">
                          <div className="space-y-2">
                            <p>No leads found</p>
                            <Button 
                              variant="outline" 
                              onClick={() => setShowNewDialog(true)}
                              className="mt-2"
                            >
                              Create your first lead
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {total > PAGE_SIZE && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100" data-testid="leads-pagination">
                <p className="text-sm text-slate-500">
                  Showing {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, total)} of {total} leads
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 px-3 text-xs"
                    disabled={page === 1}
                    onClick={() => setPage(1)}
                    data-testid="pagination-first"
                  >
                    First
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 px-3 text-xs"
                    disabled={page === 1}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    data-testid="pagination-prev"
                  >
                    Prev
                  </Button>
                  {(() => {
                    const totalPages = Math.ceil(total / PAGE_SIZE);
                    const pages = [];
                    let start = Math.max(1, page - 2);
                    let end = Math.min(totalPages, start + 4);
                    if (end - start < 4) start = Math.max(1, end - 4);
                    for (let i = start; i <= end; i++) pages.push(i);
                    return pages.map(p => (
                      <Button
                        key={p}
                        variant={p === page ? "default" : "outline"}
                        size="sm"
                        className={`h-8 w-8 text-xs ${p === page ? "bg-slate-900 text-white" : ""}`}
                        onClick={() => setPage(p)}
                        data-testid={`pagination-page-${p}`}
                      >
                        {p}
                      </Button>
                    ));
                  })()}
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 px-3 text-xs"
                    disabled={page >= Math.ceil(total / PAGE_SIZE)}
                    onClick={() => setPage(p => Math.min(Math.ceil(total / PAGE_SIZE), p + 1))}
                    data-testid="pagination-next"
                  >
                    Next
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 px-3 text-xs"
                    disabled={page >= Math.ceil(total / PAGE_SIZE)}
                    onClick={() => setPage(Math.ceil(total / PAGE_SIZE))}
                    data-testid="pagination-last"
                  >
                    Last
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bulk Action Dialogs */}
        <Dialog open={bulkDialog.open} onOpenChange={(v) => !v && setBulkDialog({ type: null, open: false })}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">
                {bulkDialog.type === "message" && "Send Bulk Message"}
                {bulkDialog.type === "stage" && "Change Stage"}
                {bulkDialog.type === "assign" && "Reassign Leads"}
                {bulkDialog.type === "tag" && "Tag Leads"}
                {bulkDialog.type === "task" && "Create Task"}
                {bulkDialog.type === "export" && "Export Leads"}
                {bulkDialog.type === "project" && "Set Project"}
                {bulkDialog.type === "score" && "Set Score"}
                {bulkDialog.type === "source" && "Set Source"}
              </DialogTitle>
              <DialogDescription className="sr-only">
                Bulk action — applies to {selectedLeads.size} selected lead{selectedLeads.size > 1 ? "s" : ""}.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="bg-slate-50 rounded-lg p-2 text-xs text-slate-600">
                <CheckSquare className="w-3.5 h-3.5 inline mr-1" />
                {selectedLeads.size} lead{selectedLeads.size > 1 ? "s" : ""} selected
              </div>

              {/* Bulk Message */}
              {bulkDialog.type === "message" && (
                <>
                  <div>
                    <Label className="text-sm">Channel</Label>
                    <div className="flex gap-2 mt-1">
                      {[{v: "whatsapp", l: "WhatsApp", c: "bg-green-600"}, {v: "sms", l: "SMS", c: "bg-blue-600"}, {v: "email", l: "Email", c: "bg-purple-600"}].map(ch => (
                        <Button key={ch.v} size="sm" variant={bulkMsgChannel === ch.v ? "default" : "outline"} className={`h-8 text-xs ${bulkMsgChannel === ch.v ? ch.c : ""}`} onClick={() => fetchTemplatesForChannel(ch.v)} data-testid={`bulk-ch-${ch.v}`}>
                          {ch.l}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm">Select Template</Label>
                    <Select value={bulkMsgTemplate} onValueChange={setBulkMsgTemplate}>
                      <SelectTrigger className="mt-1" data-testid="bulk-msg-template"><SelectValue placeholder="Choose template..." /></SelectTrigger>
                      <SelectContent>
                        {channelTemplates.map(t => (
                          <SelectItem key={t.template_id} value={t.template_id}>
                            <span className="capitalize text-[10px] text-slate-400 mr-1">{t.category?.replace(/_/g, " ")}</span> {t.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {bulkMsgTemplate && (() => {
                    const sel = channelTemplates.find(t => t.template_id === bulkMsgTemplate);
                    return sel ? (
                      <div className={`rounded-lg p-3 text-sm leading-relaxed whitespace-pre-wrap border ${
                        bulkMsgChannel === "whatsapp" ? "bg-green-50 border-green-200" : bulkMsgChannel === "sms" ? "bg-blue-50 border-blue-200" : "bg-purple-50 border-purple-200"
                      }`}>
                        {sel.subject && <p className="font-medium text-xs mb-1 opacity-70">Subject: {sel.subject}</p>}
                        {sel.content}
                      </div>
                    ) : null;
                  })()}
                </>
              )}

              {/* Bulk Stage Change */}
              {bulkDialog.type === "stage" && (
                <>
                  <div>
                    <Label className="text-sm">New Stage</Label>
                    <Select value={bulkStage} onValueChange={setBulkStage}>
                      <SelectTrigger className="mt-1" data-testid="bulk-stage-select"><SelectValue placeholder="Select stage..." /></SelectTrigger>
                      <SelectContent>
                        {["new","contacted","qualified","site_visit_scheduled","site_visit","revisit","negotiation_scheduled","negotiation","booked","re_engaged","lost"].map(s => (
                          <SelectItem key={s} value={s}>{s.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm">Reason (optional)</Label>
                    <Input value={bulkStageReason} onChange={e => setBulkStageReason(e.target.value)} placeholder="Why are you changing the stage?" className="mt-1" data-testid="bulk-stage-reason" />
                  </div>
                </>
              )}

              {/* Bulk Assign */}
              {bulkDialog.type === "assign" && (
                <div>
                  <Label className="text-sm">Assign to</Label>
                  <Select value={bulkAssignTo} onValueChange={setBulkAssignTo}>
                    <SelectTrigger className="mt-1" data-testid="bulk-assign-select"><SelectValue placeholder="Select user..." /></SelectTrigger>
                    <SelectContent>
                      {users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.role?.replace(/_/g, " ")})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Bulk Tag */}
              {bulkDialog.type === "tag" && (
                <>
                  <div className="flex gap-2">
                    <Button size="sm" variant={bulkTagAction === "add" ? "default" : "outline"} className="h-8 text-xs" onClick={() => setBulkTagAction("add")}>Add Tags</Button>
                    <Button size="sm" variant={bulkTagAction === "remove" ? "default" : "outline"} className="h-8 text-xs" onClick={() => setBulkTagAction("remove")}>Remove Tags</Button>
                  </div>
                  <div>
                    <Label className="text-sm">Tags (comma-separated)</Label>
                    <Input value={bulkTags} onChange={e => setBulkTags(e.target.value)} placeholder="e.g. hot_lead, diwali_campaign" className="mt-1" data-testid="bulk-tags-input" />
                  </div>
                </>
              )}

              {/* Bulk Task */}
              {bulkDialog.type === "task" && (
                <>
                  <div>
                    <Label className="text-sm">Task Title</Label>
                    <Input value={bulkTaskTitle} onChange={e => setBulkTaskTitle(e.target.value)} placeholder="e.g. Follow up call after Diwali" className="mt-1" data-testid="bulk-task-title" />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Type</Label>
                      <Select value={bulkTaskType} onValueChange={setBulkTaskType}>
                        <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="call">Call</SelectItem>
                          <SelectItem value="follow_up">Follow Up</SelectItem>
                          <SelectItem value="site_visit">Site Visit</SelectItem>
                          <SelectItem value="meeting">Meeting</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Priority</Label>
                      <Select value={bulkTaskPriority} onValueChange={setBulkTaskPriority}>
                        <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Due Date</Label>
                      <Input type="date" value={bulkTaskDue} onChange={e => setBulkTaskDue(e.target.value)} className="mt-1 h-8 text-xs" data-testid="bulk-task-due" />
                    </div>
                  </div>
                </>
              )}

              {/* Bulk Export */}
              {bulkDialog.type === "export" && (
                <p className="text-sm text-slate-600">This will download a CSV file containing {selectedLeads.size} selected lead{selectedLeads.size > 1 ? "s" : ""} with all their details.</p>
              )}

              {/* Bulk Set Project */}
              {bulkDialog.type === "project" && (
                <div className="space-y-2">
                  <Label className="text-xs">Project</Label>
                  <Select value={bulkProject} onValueChange={setBulkProject}>
                    <SelectTrigger data-testid="bulk-project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
                    <SelectContent>
                      {projects.map(p => (
                        <SelectItem key={p.project_id} value={p.name}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">Sets <code>project_interest</code> on {selectedLeads.size} lead{selectedLeads.size > 1 ? "s" : ""}. Existing values will be overwritten.</p>
                </div>
              )}

              {/* Bulk Set Score */}
              {bulkDialog.type === "score" && (
                <div className="space-y-2">
                  <Label className="text-xs">Score</Label>
                  <Select value={bulkScore} onValueChange={setBulkScore}>
                    <SelectTrigger data-testid="bulk-score-select"><SelectValue placeholder="Select score" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hot">🔥 Hot (80)</SelectItem>
                      <SelectItem value="warm">☀️ Warm (55)</SelectItem>
                      <SelectItem value="nurture">🌱 Nurture (30)</SelectItem>
                      <SelectItem value="cold">❄️ Cold (15)</SelectItem>
                      <SelectItem value="dormant">💤 Dormant (10)</SelectItem>
                      <SelectItem value="new">✨ New (50)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">Sets both categorical <code>score</code> and numeric <code>score_value</code>. The lead becomes <em>locked</em> from auto re-scoring.</p>
                </div>
              )}

              {/* Bulk Set Source */}
              {bulkDialog.type === "source" && (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Source</Label>
                    <Select value={bulkSourceVal} onValueChange={(v) => { setBulkSourceVal(v); setBulkSourceSubVal(""); }}>
                      <SelectTrigger data-testid="bulk-source-select"><SelectValue placeholder="Select source" /></SelectTrigger>
                      <SelectContent>
                        {SOURCES.map(s => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Sub-source <span className="text-slate-400 font-normal">(optional)</span></Label>
                    <Input
                      value={bulkSourceSubVal}
                      onChange={(e) => setBulkSourceSubVal(e.target.value)}
                      placeholder="e.g. campaign_x, agent_name"
                      className="mt-1 h-8 text-xs"
                      data-testid="bulk-source-sub"
                    />
                  </div>
                  <p className="text-xs text-slate-500">Writes <code>latest_source</code> (preserving the original first-touch <code>source</code>). For freshly-imported leads with no source, this becomes their attribution.</p>
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setBulkDialog({ type: null, open: false })}>Cancel</Button>
                <Button onClick={handleBulkAction} disabled={bulkLoading} data-testid="bulk-confirm-btn">
                  {bulkLoading ? "Processing..." : bulkDialog.type === "export" ? "Download CSV" : "Confirm"}
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default LeadsPage;
